// src/axios-config.js
import axios from 'axios';
import apiConfig from '@/apiConfig';
import store from '@/store';
import router from '@/router'; // Import router directly

// Create an Axios instance using the base URL from the config file
const apiClient = axios.create({
  baseURL: apiConfig.BASE_URL, // Use base URL from apiConfig
  headers: { 'Content-Type': 'application/json' },
});

// Keep track of refresh token promise to prevent multiple token refreshes
let refreshTokenPromise = null;

// Create token refresh function
const refreshToken = async () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = store.dispatch('refreshToken')
      .finally(() => {
        refreshTokenPromise = null;
      });
  }
  return refreshTokenPromise;
};

// Request interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Add debug logging
    console.log('API Error Response:', error.response?.data);
    
    const isTokenExpired = 
      error.response?.status === 401 || 
      error.response?.data?.code === 'TOKEN_EXPIRED';

    const isUnauthorizedAccess = error.response?.data?.code === 'ADMIN_ONLY';
    
    // Add debug logging
    console.log('Is Unauthorized Access:', isUnauthorizedAccess);

    if (isUnauthorizedAccess) {
      console.log('Setting authorization error:', error.response.data.message);
      store.commit('setAuthorizationError', error.response.data.message);
      
      // Handle unauthorized access
      await store.dispatch('logout');
      
      // Use router.push directly
      if (router.currentRoute.value.name !== 'Login') {
        router.push('/login');
      }
      
      return Promise.reject(error);
    }

    // If token is expired and we haven't tried to refresh token yet
    if (isTokenExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Refresh token
        await refreshToken();
        
        // Update authorization header
        const newToken = localStorage.getItem('auth_token');
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, logout user
        await store.dispatch('logout');
        if (router.currentRoute.value.name !== 'Login') {
          router.push('/login');
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
